package com.islamicmedia.shared.domain.model

/**
 * Whether THIS device has already acknowledged (tapped OK on) the one-time "your referral was
 * blocked" notice (com.islamicmedia.shared.presentation.ReferralCopy.REFERRAL_BLOCKED) for one
 * (account+backend, block) combination — [owner] as [ReferralInfoCache.owner] keys it, [blockedAt]
 * as GET /api/referral/me's referral_blocked_at names the block (owner's report, 2026-09-25).
 *
 * Keyed on the PAIR, not just the owner (unlike its sibling [ReferralRedeemPromptSeen]): a
 * referral can be blocked more than once for the same account over time (a second friend's
 * payment, a later card match), and each is its own notice — acknowledging one must not silently
 * swallow the next. Reuses [ReferralRedeemPromptSeen]'s set-of-strings encoding with a compound
 * key, so the same 30-day-window and multi-account behaviour carries over for free.
 *
 * Every SettingsStorage actual persists the encoded form under one key
 * (SettingsStorage.hasAcknowledgedReferralBlocked / markReferralBlockedAcknowledged) and, like its
 * sibling, is NOT cleared by clearUserData: it is already scoped per account+block, so signing out
 * must not un-acknowledge it for the account that just left.
 */
internal object ReferralBlockedAck {
    /** True when [owner] has already acknowledged the block at [blockedAt]. */
    fun hasAcknowledged(raw: String?, owner: String, blockedAt: String): Boolean =
        ReferralRedeemPromptSeen.hasSeen(raw, key(owner, blockedAt))

    /** [raw] with (owner, blockedAt) added, ready to persist. Idempotent, like its sibling. */
    fun acknowledge(raw: String?, owner: String, blockedAt: String): String =
        ReferralRedeemPromptSeen.markSeen(raw, key(owner, blockedAt))

    /** Opaque compound key: never parsed back apart, only compared for membership. */
    private fun key(owner: String, blockedAt: String): String = "$owner#$blockedAt"
}
